import React, { useEffect, useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import DashboardHeader from "../components/Other/DashboardHeader";
import PageHeaderTitle from "../components/pages/PageHeaderTitle";
import PageContainer from "../components/pages/PageContainer";
import { SkeletonBlock } from "../components/Skeleton/Skeleton";
import ChatThread from "../components/Chats/ChatThread";
import Composer from "../components/Chats/Composer";
import ThreadHeader from "../components/Chats/ThreadHeader";
import useChatThread from "../components/Chats/useChatThread";

// Chats Detail — ONE individual-conversation screen for the whole app.
//
// Client: "Please make the direct message experience consistent everywhere,
// regardless of whether the user enters from the main QUSHi site or from inside
// League Play, and regardless of mobile or desktop… The main goal here is to
// make every individual conversation screen use one consistent system across
// QUSHi and League Play."
//
// So `game_id` no longer selects a different layout. It is what it always should
// have been: the SCOPE of the messages (a league DM is a separate thread from the
// same two people's main-app DM), and nothing else. The league used to take its
// own branch here — the app header with the league avatar and the QUSHi logo, the
// league's GameSideBar menu column beside the thread, and no back arrow at all —
// which is the "old page" on desktop and the wrong header on mobile.
//
// Layout, now identical at both addresses: sticky header, composer pinned above
// the safe area, messages scrolling between them. Phones get a fixed pane that
// starts at the top of the viewport so the composer can't be pushed off-screen;
// desktop gets a fixed-height card in the normal content column.
const ChatHistoryPage = (props) => {
  const game_id = props.game_id ?? null;
  const slug = props.slug ?? null;
  const navigate = useNavigate();
  const [sidebarToggle] = useOutletContext();
  const { chatid, chatgid } = useParams();

  // Three ways in, one receiver:
  //   /chatHistory/:chatid[/:chatgid]  — the Chats list, and Profile's Message
  //   /:slug/chatHistory/:user_id      — a League Profile's Message (prop)
  //   /chatHistory                     — whatever the list last stashed
  const receiverId = chatid || props.profile_id || localStorage.getItem("id");
  const chatGroupId = chatgid || localStorage.getItem("gid");

  const me = (() => {
    try {
      return JSON.parse(localStorage.getItem("userInfo"))?.id ?? null;
    } catch (e) {
      return null;
    }
  })();

  const {
    messages,
    friend: friendFromApi,
    loading,
    sending,
    sendText,
    sendAttachment,
  } = useChatThread({ receiverId, chatGroupId, gameId: game_id });

  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const isDesktop = width >= 1024;
  // Phone treatment: the conversation header IS the header, so the app chrome row
  // above it is not rendered at all. 768 is the cut-off because from there up the
  // sidebar handles navigation and the thread keeps the normal app header. Inside
  // a league this now applies too — client, on the League Play DM header: "the
  // current header shows the league avatar and QUSHi logo. Please remove that
  // treatment. Use the same direct message header as everywhere else."
  const isPhoneThread = width < 768;

  // Who we're talking to. get-chat-detail now returns them outright, which is the
  // only source that works for every thread. The two fallbacks below cover a
  // backend that has not shipped that field yet: the sender of the first incoming
  // message, then the avatar the list stashed. Both are why the header used to
  // show a bare avatar with no username and no "Last active" on a thread the user
  // had only ever sent into.
  const friend =
    friendFromApi ||
    (() => {
      const incoming = messages.find(
        (m) => String(m?.user_id) !== String(me) && m?.user
      );
      if (incoming?.user) return incoming.user;
      const image = localStorage.getItem("image");
      return image ? { image } : null;
    })();

  // "The back arrow on an individual conversation should return the user to the
  // previous screen they came from… It should not automatically take them to the
  // Chats home screen."
  //
  // This is also the never-ending loop the client reported, and it is not the
  // API 36 upgrade. The arrow used to navigate("/chat"), which PUSHES the Chats
  // home screen on top of the conversation; the Chats home screen's own arrow is
  // navigate(-1), which goes straight back down to the conversation. Back, back,
  // back forever, with no way out of the pair.
  //
  // Popping the entry this screen was pushed onto satisfies both: the user lands
  // on whatever they came from (a profile, a League Profile, the Chats list) and
  // no new entry is stacked, so nothing can bounce. The fallback is only for a
  // conversation opened with no history behind it — a deep link, or the native
  // wrapper opening a notification cold — and it replaces rather than pushes, for
  // the same reason.
  const fallbackBack =
    game_id && slug && receiverId ? `/${slug}/profile/${receiverId}` : "/chat";

  const goBack = () => {
    // React Router keeps its position in history as state.idx; > 0 means there is
    // an in-app entry behind us that a pop will land on.
    const idx = window?.history?.state?.idx;
    if (typeof idx === "number" && idx > 0) navigate(-1);
    else navigate(fallbackBack, { replace: true });
  };

  const thread = (
    <>
      <ThreadHeader user={friend} onBack={goBack} />
      {loading ? (
        <div
          className="flex min-h-0 flex-1 flex-col justify-end gap-3 p-4"
          aria-busy="true"
          aria-label="Messages loading"
        >
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className={`flex ${i % 2 ? "justify-end" : "justify-start"}`}
            >
              <SkeletonBlock
                rounded="rounded-2xl"
                height={i % 3 === 0 ? 52 : 36}
                width={i % 2 ? "58%" : "64%"}
              />
            </div>
          ))}
        </div>
      ) : (
        <ChatThread
          messages={messages}
          myId={me}
          // Bubble max width 75% of screen width on mobile.
          maxBubbleClass={isDesktop ? "max-w-[60%]" : "max-w-[75%]"}
          className="min-h-0 flex-1"
        />
      )}
      {/* shrink-0 so a composer grown to several lines takes its space from the
          message list rather than being squashed. */}
      <div
        className="shrink-0"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <Composer
          onSend={sendText}
          onAttach={sendAttachment}
          disabled={sending}
        />
      </div>
    </>
  );

  // Phones: no app chrome row above the conversation, so the pane starts at the
  // very top of the viewport.
  if (isPhoneThread) {
    return (
      <div className="fixed inset-0 z-[5] flex flex-col bg-white">{thread}</div>
    );
  }

  return (
    <>
      <DashboardHeader toggle={sidebarToggle} />
      {/* The conversation header (avatar + handle) identifies the screen, so no
          desktop title strip and no mobile under-logo title. */}
      <PageHeaderTitle title="Chat" showDesktopTitle={false} />

      {isDesktop ? (
        <PageContainer>
          <div className="mx-auto mb-12 w-full max-w-4xl pt-10">
            <div className="flex h-[calc(100vh-14rem)] min-h-[28rem] flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white">
              {thread}
            </div>
          </div>
        </PageContainer>
      ) : (
        // Tablet only (768–1023): phones return early above and ≥1024 gets the
        // card. Fixed pane below the app bar — ~64px tall in its md layout — and
        // clear of the sidebar, which is on screen from 768 up.
        <div className="fixed bottom-0 left-64 right-0 top-16 z-[5] flex flex-col bg-white">
          {thread}
        </div>
      )}
    </>
  );
};

export default ChatHistoryPage;
